import { store } from '../createStore';
import { buildDesignerStorageUnitUpdate } from '../store/storageunits/buildStorageUnitUpdate';
import { StorageUnitType, type StorageUnit } from '../store/storageunits/types';
import { FileStatus } from '../store/fileState/types';
import { storageUnitCache } from '../util/cache/storageUnitCacheIndexedDB';
import { getCurrentUserId } from './userIdentity';

const documentChanged = (snapshot: ReturnType<typeof store.getState>) => {
  const latest = store.getState();
  return (
    latest.unitConnGroup !== snapshot.unitConnGroup ||
    latest.settingsState !== snapshot.settingsState ||
    latest.documentMetadata !== snapshot.documentMetadata ||
    latest.storageUnitsState.currentFile.id !==
      snapshot.storageUnitsState.currentFile.id
  );
};

/** Finish a local checkpoint before login redirects away from the editor. */
export async function saveBeforeSignIn() {
  const snapshot = store.getState();
  if (!snapshot.fileState.fileHasBeenEdited) return;
  // Roles are unavailable once Keycloak clears a rejected session, so only the
  // file status decides. Edits that cannot be queued must block the redirect
  // rather than be discarded.
  const { fileStatus } = snapshot.fileState;
  if (fileStatus !== FileStatus.EDIT && fileStatus !== FileStatus.TEMPLATE)
    throw new Error(
      'This document cannot be saved. Download it, then sign in.',
    );
  const currentFile = snapshot.storageUnitsState.currentFile;
  if (!currentFile.id) throw new Error('Save your document before signing in.');
  const owner = getCurrentUserId();
  const existing = await storageUnitCache.getOne(currentFile.id);
  if (owner !== getCurrentUserId())
    throw new Error('The account changed. Please try again.');
  if (documentChanged(snapshot))
    throw new Error('The document changed while saving. Please try again.');
  const now = new Date();
  const update = buildDesignerStorageUnitUpdate({
    currentFile,
    ...snapshot.unitConnGroup.present,
    settingsState: snapshot.settingsState,
    documentMetadata: snapshot.documentMetadata,
    fileState: snapshot.fileState,
  });
  const pendingAction =
    existing?.pendingAction === 'create' ? 'create' : 'update';
  const saved: StorageUnit = {
    createdAt: now,
    ...existing,
    ...currentFile,
    ...update,
    name: update.name ?? '',
    type: StorageUnitType.File,
    updatedAt: now,
    isSynced: false,
    pendingAction,
    localUpdatedAt: now.toISOString(),
  };
  await storageUnitCache.setOne(saved, {
    markSynced: false,
    pendingAction,
    throwOnError: true,
  });
  if (owner !== getCurrentUserId())
    throw new Error('The account changed. Please try again.');
  if (documentChanged(snapshot))
    throw new Error('The document changed while saving. Please try again.');
}
